/**
 * Types for MarkdownWithComponents
 *
 * Provides the type system for the markdown component framework:
 * - Segment types for the parser output
 * - Action types for interactive component callbacks
 * - Registry types for component registration
 * - Type guards for built-in actions
 */

// ---------------------------------------------------------------------------
// Parser segment types
// ---------------------------------------------------------------------------

export interface MarkdownSegment {
  type: 'markdown'
  content: string
}

export interface ComponentSegment {
  type: 'component'
  componentType: string
  props: Record<string, string>
}

export type Segment = MarkdownSegment | ComponentSegment

// ---------------------------------------------------------------------------
// Action types
// ---------------------------------------------------------------------------

/**
 * Base action type for all markdown component interactions.
 * Built-in components fire well-structured payloads; custom components
 * can pass any shape. Use type guards (e.g. isConfirmAction) to narrow.
 */
export interface MarkdownComponentAction {
  type: string
  [key: string]: unknown
}

/**
 * Action fired by MarkdownConfirm when a button is clicked.
 */
export interface ConfirmAction extends MarkdownComponentAction {
  type: 'confirm'
  /** The yes_prompt or no_prompt value from the clicked button */
  prompt: string
  /** True if the user clicked the affirmative button */
  confirmed: boolean
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export function isConfirmAction(
  action: MarkdownComponentAction
): action is ConfirmAction {
  return (
    action.type === 'confirm' &&
    typeof action.prompt === 'string' &&
    typeof action.confirmed === 'boolean'
  )
}

// ---------------------------------------------------------------------------
// Component registry types
// ---------------------------------------------------------------------------

/**
 * Props that every markdown component receives from MarkdownWithComponents.
 * Individual components extend this with their own parsed props.
 */
export interface MarkdownComponentBaseProps {
  onAction: (action: MarkdownComponentAction) => void
}

/**
 * Map of component type names to React components.
 * Keys correspond to the name after `ui:` in the fenced block.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type MarkdownComponentMap = Record<string, React.ComponentType<any>>
